/**
 * ContactInfo - Modal displaying contact and support information
 */

import {
  Text,
  Bold,
  Muted,
  VerticalSpace,
  Divider,
  Modal,
} from "../../ui-kit";
import { useState } from "react";

export interface ContactInfoProps {
  open: boolean;
  onClose: () => void;
}

// Copy icon component
const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

// Discord icon component
const DiscordIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
  </svg>
);

// Book/Blog icon component
const BlogIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

function ContactInfo({ open, onClose }: ContactInfoProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText("support@tomake.design");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = "support@tomake.design";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDiscordClick = () => {
    window.open("https://discord.gg/tomakedesign", "_blank");
  };

  const handleDocsClick = () => {
    window.open("https://tomake.design/docs", "_blank");
  };

  const linkStyle = {
    color: "var(--figma-color-text-brand)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    textDecoration: "none",
  };

  return (
    <Modal position="bottom" open={open} onCloseButtonClick={onClose} title="Help & Support">
      <div style={{ padding: "16px", minWidth: "300px" }}>
        {/* Support & Billing Section */}
        <div>
          <Text>
            <Bold>Support & Billing Questions</Bold>
          </Text>
          <VerticalSpace space="small" />
          <Text>
            <Muted>
              Need help with html.tomake.design (llama), or have a billing question? Send me an email.
            </Muted>
          </Text>
          <VerticalSpace space="small" />
          <div
            style={linkStyle}
            onClick={handleCopyEmail}
            title={copied ? "Copied!" : "Click to copy"}
          >
            <Text style={{ color: "var(--figma-color-text-brand)" }}>
              support@tomake.design
            </Text>
            <span style={{ opacity: 0.7 }}>
              <CopyIcon />
            </span>
            {copied && (
              <Text style={{ fontSize: "11px", color: "var(--figma-color-text-success)" }}>
                Copied!
              </Text>
            )}
          </div>
        </div>

        <VerticalSpace space="large" />
        <Divider />
        <VerticalSpace space="large" />

        {/* Community Section */}
        <div>
          <Text>
            <Bold>html.tomake.design Community</Bold>
          </Text>
          <VerticalSpace space="small" />
          <Text>
            <Muted>
              A place to ask questions, share your work, and get help.
            </Muted>
          </Text>
          <VerticalSpace space="small" />
          <div style={linkStyle} onClick={handleDiscordClick}>
            <Text style={{ color: "var(--figma-color-text-brand)" }}>
              Join the Discord
            </Text>
            <span style={{ opacity: 0.7 }}>
              <DiscordIcon />
            </span>
          </div>
        </div>

        <VerticalSpace space="large" />
        <Divider />
        <VerticalSpace space="large" />

        {/* Blog Section */}
        <div>
          <Text>
            <Bold>Documentation</Bold>
          </Text>
          <VerticalSpace space="small" />
          <Text>
            <Muted>
              Find guides, tutorials, and tips on using html.tomake.design.
            </Muted>
          </Text>
          <VerticalSpace space="small" />
          <div style={linkStyle} onClick={handleDocsClick}>
            <Text style={{ color: "var(--figma-color-text-brand)" }}>
              Visit the tomake.design (Docs)
            </Text>
            <span style={{ opacity: 0.7 }}>
              <BlogIcon />
            </span>
          </div>
        </div>

        <VerticalSpace space="large" />
        <Divider />
        <VerticalSpace space="medium" />

        {/* Version Info */}
        <div style={{ textAlign: "center" }}>
          <Text>
            <Muted style={{ fontSize: "10px" }}>Version 2025.1.3</Muted>
          </Text>
        </div>
      </div>
    </Modal>
  );
}

export default ContactInfo;
